import axios from 'axios';
import {
  ApiResponse,
  EOIFormData,
  EoiGuestSubmitRequest,
  ExpressionOfInterestListItem,
  ExpressionOfInterest,
  PaginatedResponse,
} from '@court-sync/shared';
import { API_BASE_URL } from '../config/api';
import { auth } from '../config/firebase';

async function authHeaders(): Promise<Record<string, string>> {
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : '';
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
}

export interface ProgramAccessResult {
  canBookDirectly: boolean;
  studentAccess: Record<string, boolean>;
}

export const eoiService = {
  /** Invitation-only programs — submit EOI without an account (rate-limited on server). */
  async submitGuestEOI(body: EoiGuestSubmitRequest): Promise<ApiResponse<ExpressionOfInterest>> {
    const response = await axios.post(`${API_BASE_URL}/public/eoi/guest`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  async submitEOI(
    body: EOIFormData & { academyId: string; lessonId: string; studentId: string }
  ): Promise<ApiResponse<ExpressionOfInterest>> {
    const response = await axios.post(`${API_BASE_URL}/public/eoi`, body, {
      headers: await authHeaders(),
    });
    return response.data;
  },

  async getProgramAccess(
    lessonId: string,
    academyId: string,
    studentIds: string[]
  ): Promise<ApiResponse<ProgramAccessResult>> {
    const response = await axios.get(`${API_BASE_URL}/public/lessons/${lessonId}/program-access`, {
      params: { academyId, studentIds: studentIds.join(',') },
      headers: await authHeaders(),
    });
    return response.data;
  },

  async listEOIs(
    academyId: string,
    filters?: { programId?: string; lessonId?: string; termId?: string; status?: string },
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<ExpressionOfInterestListItem>> {
    const api = axios.create({ baseURL: API_BASE_URL, headers: await authHeaders() });
    const response = await api.get<PaginatedResponse<ExpressionOfInterestListItem>>(
      '/eoi',
      {
        params: { academyId, ...filters, page, limit },
      }
    );
    return response.data;
  },

  async sendInvitation(eoiId: string, academyId: string): Promise<ApiResponse<ExpressionOfInterest>> {
    const api = axios.create({ baseURL: API_BASE_URL, headers: await authHeaders() });
    const response = await api.post<ApiResponse<ExpressionOfInterest>>(
      `/eoi/${eoiId}/invite`,
      {},
      { params: { academyId } }
    );
    return response.data;
  },

  async declineEOI(eoiId: string, academyId: string, notes?: string): Promise<ApiResponse> {
    const api = axios.create({ baseURL: API_BASE_URL, headers: await authHeaders() });
    const response = await api.post<ApiResponse>(`/eoi/${eoiId}/decline`, { academyId, notes });
    return response.data;
  },
};
